import React from 'react';

import { formatPhoneNumber } from '@/shared/utils';

import { getInitials } from '../../../utils/dateFormatter';
import { formatShortDate } from '../detailsFormat';
import type { DetailsData } from '../useDetailsData';

interface MembersLayerProps {
  data: DetailsData;
}

/**
 * Camada MEMBROS — só existe em conversa de grupo.
 *
 * A lista vem inteira dentro da própria conversa (`conversation.members`), não
 * há busca própria: quem abre a camada já tem o dado em mãos ou não tem nenhum.
 * Por isso o estado vazio fala de "ainda não carregados" em vez de "grupo sem
 * gente" — grupo sem gente não existe; o que existe é conversa antiga cujo
 * provedor nunca mandou a lista.
 */

/** Ausência vira travessão. Mesmo símbolo que os formatadores devolvem. */
const TRACO = '—';

/** Avatar de 26px: o do topo tem 36px, este fica um degrau abaixo. */
const AVATAR: React.CSSProperties = {
  width: 26,
  height: 26,
  borderRadius: '32%',
  flex: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '.02em',
  // As duas únicas cores fixas do painel, herdadas dos avatares que a base já
  // usa: a tinta ardósia e o branco em cima dela funcionam nos dois temas.
  backgroundColor: '#64748b',
  color: '#ffffff',
  overflow: 'hidden',
};

const CONTAGEM: React.CSSProperties = {
  fontSize: 11,
  lineHeight: 1.3,
  fontWeight: 500,
  color: 'var(--theme-muted-text)',
  marginBottom: 8,
};

const NOME: React.CSSProperties = {
  fontSize: 13,
  lineHeight: 1.3,
  fontWeight: 500,
  color: 'var(--theme-card-text)',
};

const DETALHE: React.CSSProperties = {
  fontSize: 11,
  lineHeight: 1.3,
  color: 'var(--theme-muted-text)',
};

/**
 * Quem administra ganha a tinta roxa — a mesma de "nosso time" que o avatar do
 * atendente já usa. Sem ela a pílula de administrador e a de membro ficariam
 * idênticas e o papel deixaria de ser lido de relance.
 */
const PILULA_ADMIN: React.CSSProperties = {
  background: 'color-mix(in srgb, var(--theme-primary) 15%, var(--theme-sidebar-background))',
  borderColor: 'color-mix(in srgb, var(--theme-primary) 30%, transparent)',
  color: 'var(--theme-primary)',
};

const MembersLayer: React.FC<MembersLayerProps> = ({ data }) => {
  const members = data.conversation?.members ?? [];

  if (members.length === 0) {
    return (
      <div data-testid="details-members">
        <p className="cdp-empty">Os participantes deste grupo ainda não foram carregados.</p>
      </div>
    );
  }

  const contagem = `${members.length} ${members.length === 1 ? 'pessoa' : 'pessoas'}`;
  // A data de criação do grupo é a mesma que a faixa de números do topo mostra.
  // Só entra quando existe: "grupo criado em —" não informa nada.
  const criadoEm = formatShortDate(data.conversation?.createdAt);
  const cabecalho = criadoEm === TRACO ? contagem : `${contagem} · grupo criado em ${criadoEm}`;

  return (
    <div data-testid="details-members">
      <p style={CONTAGEM}>{cabecalho}</p>

      {members.map((member, index) => {
        const nome = (member.name ?? '').trim() || TRACO;
        const telefone = member.phone?.trim();
        const entradaEm = formatShortDate(member.joinedAt);

        // Monta só com os pedaços que existem: sem isso a linha viraria
        // "Entrou em —" em membro cuja data o provedor nunca mandou.
        const pedacos: string[] = [];
        if (telefone) pedacos.push(formatPhoneNumber(telefone) || telefone);
        if (entradaEm !== TRACO) pedacos.push(`Entrou em ${entradaEm}`);
        const detalhe = pedacos.length > 0 ? pedacos.join(' · ') : TRACO;

        const ehAdmin = member.role === 'admin';

        return (
          <div key={member.id} className="cdp-line" data-testid={`details-member-${index}`}>
            <span style={AVATAR} aria-hidden="true">
              {/* Com foto o quadrado é só a moldura dela; sem foto, a inicial. */}
              {member.picture ? (
                <img
                  src={member.picture}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                getInitials(member.name)
              )}
            </span>

            <div className="flex-1 min-w-0">
              <div className="truncate" style={NOME}>
                {nome}
              </div>
              <div className="truncate" style={DETALHE}>
                {detalhe}
              </div>
            </div>

            <span className="cdp-tag" style={ehAdmin ? PILULA_ADMIN : undefined}>
              {ehAdmin ? 'Admin' : 'Membro'}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default MembersLayer;
